import { tv } from 'tailwind-variants';

// Centralised Tailwind Variants for the Election Candidates component (server + client share these slots)
export const electionCandidatesVariants = tv({
  slots: {
    // Top control bar holding the category toggle and the voting-area selector
    controlBar: [
      'flex',
      'flex-col',
      'gap-2',
      'xm:flex-row',
      'xm:items-start',
      'xm:justify-between',
      'bg-light-gray',
      'rounded-xl',
      'py-[15px] px-5 xm:p-5',
      'mb-8',
    ],
    controlGroup: ['flex', 'flex-col', 'gap-2'],
    controlLabel: [
      'text-[13px]',
      'font-medium',
      'leading-[19.5px]',
      'uppercase',
      'text-textPrimary',
    ],
    buttonRow: ['flex', 'gap-1', 'md:ml-auto'],
    // Shared base for the toggle/area buttons; active state swaps to the primary fill
    toggleButton: [
      'w-10 md:w-8 ml:w-10',
      'shrink-0',
      'py-2.5',
      'rounded',
      'border',
      'border-medium-gray',
      'bg-white',
      'text-sm',
      'font-medium',
      'leading-[21px]',
      'text-textPrimary',
      'cursor-pointer',
      'transition-colors',
      'hover:border-secondary',
      'hover:bg-secondary',
      'hover:text-white',
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-offset-2',
      'focus-visible:outline-secondary',
    ],
    toggleButtonActive: ['bg-secondary!', 'border-secondary!', 'text-white!'],
    // Heading row: large election title + right-aligned voting-area pill
    headingRow: [
      'flex',
      'flex-col',
      'gap-2.5',
      'md:flex-row',
      'md:items-center',
      'md:justify-between',
      'mb-8',
    ],
    heading: [
      'mx-auto md:mx-0',
      'text-left',
      'text-textPrimary',
      'text-[27.2px]',
      'leading-7.5',
      'font-semibold',
    ],
    areaBadge: [
      'mx-auto md:mx-0',
      'inline-block',
      'self-start',
      'md:self-auto',
      'bg-secondary/10',
      'text-base',
      'font-semibold',
      'tracking-[0.5px]',
      'leading-6',
      'text-secondary',
      'uppercase',
      'px-6',
      'py-2.5',
      'rounded',
    ],
    // A single position group (e.g. "President") with its candidate cards
    positionGroup: ['mb-6', 'border-b-2', 'border-textPrimary', 'pb-7.5', 'last:border-b-0'],
    positionTitle: ['text-textPrimary', 'text-6', 'leading-[28.8px]', 'mb-[15px]'],
    candidateList: ['flex', 'flex-col', 'gap-4'],
    candidateCard: [
      // 'relative' anchors the stretched bio link overlay so the whole card is clickable;
      // 'isolate' confines the overlay/button z-index stack to this card
      'relative',
      'isolate',
      'flex',
      'flex-col',
      'gap-4',
      'sm:flex-row',
      'sm:items-center',
      'sm:justify-between',
      'border',
      'border-medium-gray',
      'rounded',
      'py-[15px] px-5 md:py-5 md:px-6',
      'transition-all',
      'duration-200',
    ],
    candidateInfo: ['flex', 'flex-col', 'gap-1'],
    candidateName: ['text-xl!', 'font-bold', 'text-textPrimary', 'leading-6'],
    metaRow: ['flex', 'flex-wrap', 'items-center', 'gap-2.5'],
    winnerBadge: [
      'inline-block',
      'bg-dark-green',
      'text-white',
      'font-bold',
      'uppercase',
      'text-xs',
      'leading-3',
      'px-3',
      'py-1.5',
      'rounded',
    ],
    votesBadge: [
      'inline-block',
      'bg-light-gray',
      'text-textPrimary',
      'font-semibold',
      'uppercase',
      'text-xs',
      'leading-3',
      'px-3',
      'py-1.5',
      'rounded',
    ],
    // Anchor stays static (no positioning) so its ::after overlay sizes to the whole card,
    // keeping the entire card a click target. No visual hover lives here.
    bioLink: [
      'inline-flex',
      'items-center',
      'justify-center',
      'shrink-0',
      'rounded',
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-offset-2',
      'focus-visible:outline-secondary',
      // Stretch the anchor's hit area over the entire card via an absolute pseudo-element
      "after:content-['']",
      'after:absolute',
      'after:inset-0',
      'after:rounded',
      'after:z-0',
    ],
    // Visible button lifted above the overlay (z-1) so it is hovered only on direct cursor-over,
    // scoping the color change to the button instead of the whole card
    bioButton: [
      'relative',
      'z-[1]',
      'inline-flex',
      'items-center',
      'justify-center',
      'bg-primary',
      'text-white',
      'text-[14.4px]',
      'font-medium',
      'leading-[21.6px]',
      'px-5',
      'py-2.5',
      'rounded',
      'transition-colors',
      'hover:bg-tertiary',
    ],
    emptyState: ['text-lg', 'font-light', 'leading-[27px]', 'text-light-black'],
  },
  variants: {
    // Only clickable cards (bio PDF available) get the hover shadow + pointer cursor
    clickable: {
      true: {
        candidateCard: ['cursor-pointer', 'hover:shadow-[0_0_20px_rgba(0,0,0,0.0980)]'],
      },
      false: {
        candidateCard: [],
      },
    },
    // Opt-in wrapping for a long button row (e.g. many voting areas). The category "View By"
    // row leaves this off so it always renders on a single line.
    scrollable: {
      true: {
        // min-w-0 lets only this group shrink inside the flex control bar so its buttons can wrap
        controlGroup: ['min-w-0'],
        // Wrap overflowing buttons onto new lines instead of overflowing the row
        buttonRow: ['flex-wrap'],
      },
    },
  },
  defaultVariants: {
    clickable: false,
  },
});
